import React, { useEffect, useState } from "react"
import { supabase } from "../../../lib/supabaseClient"

// ── Types ──────────────────────────────────────────────

type Summary = {
  total_revenue: number
  total_orders: number
  total_paid: number
  total_pending: number
  total_cancelled: number
  avg_order_value: number
  total_coupon_discounts: number
  total_partner_commissions: number
  orders_with_coupon: number
  partner_orders: number
}

type YearlyRow = {
  year: number
  revenue: number
  order_count: number
  paid_count: number
  avg_order_value: number
  coupon_discounts: number
  partner_commissions: number
}

type MonthlyRow = {
  month: number
  revenue: number
  order_count: number
  paid_count: number
  avg_order_value: number
}

type ServiceRow = { service_name: string; revenue: number; order_count: number }
type StatusRow = { status: string; revenue: number; order_count: number }
type PartnerRow = {
  partner_id: string
  partner_name: string
  revenue: number
  order_count: number
  commission_total: number
}

// ── Helpers ────────────────────────────────────────────

const MONTH_NAMES = [
  "", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
]

const STATUS_LABELS: Record<string, string> = {
  EN_ATTENTE: "⏳ En attente",
  EN_COURS: "🔧 En cours",
  PRET_A_RECUPERER: "✅ Prêt",
  EN_LIVRAISON: "🚚 En livraison",
  LIVREE: "📦 Livrée",
  LIVRE: "📦 Livrée",
  ANNULEE: "❌ Annulée",
}

const chf = (n: number) => n.toFixed(2) + " CHF"

// ── Inline styles ──────────────────────────────────────

const s = {
  page: { maxWidth: 1100, margin: "0 auto", padding: "1.5rem" } as React.CSSProperties,
  title: { fontSize: "1.6rem", fontWeight: 700, marginBottom: "1.5rem" } as React.CSSProperties,
  kpiGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: "1rem", marginBottom: "2rem",
  } as React.CSSProperties,
  kpiCard: (clr: string): React.CSSProperties => ({
    background: clr + "10", border: "1px solid " + clr + "30", borderRadius: ".75rem",
    padding: "1rem 1.25rem", display: "flex", flexDirection: "column", gap: ".25rem",
  }),
  kpiVal: (clr: string): React.CSSProperties => ({ fontSize: "1.5rem", fontWeight: 700, color: clr }),
  kpiLbl: {
    fontSize: ".8rem", color: "#64748b", fontWeight: 500,
    textTransform: "uppercase" as const, letterSpacing: ".04em",
  } as React.CSSProperties,
  section: { marginBottom: "2rem" } as React.CSSProperties,
  sectionHead: {
    display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: ".75rem",
  } as React.CSSProperties,
  sectionTitle: { fontSize: "1.1rem", fontWeight: 600, color: "#1e293b", margin: 0 } as React.CSSProperties,
  card: {
    background: "white", border: "1px solid #e2e8f0", borderRadius: ".75rem", overflow: "hidden",
  } as React.CSSProperties,
  table: { width: "100%", borderCollapse: "collapse" as const, fontSize: ".9rem" } as React.CSSProperties,
  th: {
    textAlign: "left" as const, padding: ".75rem 1rem", borderBottom: "2px solid #e2e8f0",
    color: "#64748b", fontWeight: 600, fontSize: ".8rem",
    textTransform: "uppercase" as const, letterSpacing: ".03em", background: "#f8fafc",
  } as React.CSSProperties,
  thR: {
    textAlign: "right" as const, padding: ".75rem 1rem", borderBottom: "2px solid #e2e8f0",
    color: "#64748b", fontWeight: 600, fontSize: ".8rem",
    textTransform: "uppercase" as const, letterSpacing: ".03em", background: "#f8fafc",
  } as React.CSSProperties,
  td: { padding: ".75rem 1rem", borderBottom: "1px solid #f1f5f9", color: "#334155" } as React.CSSProperties,
  tdR: {
    padding: ".75rem 1rem", borderBottom: "1px solid #f1f5f9", color: "#334155",
    textAlign: "right" as const, fontVariantNumeric: "tabular-nums",
  } as React.CSSProperties,
  tdB: {
    padding: ".75rem 1rem", borderBottom: "1px solid #f1f5f9", color: "#1e293b",
    textAlign: "right" as const, fontWeight: 600, fontVariantNumeric: "tabular-nums",
  } as React.CSSProperties,
  btn: {
    padding: ".4rem .85rem", background: "#3b82f6", color: "white",
    border: "none", borderRadius: ".375rem", cursor: "pointer", fontWeight: 500, fontSize: ".85rem",
  } as React.CSSProperties,
  btnGhost: {
    padding: ".4rem .85rem", background: "#f1f5f9", color: "#475569",
    border: "1px solid #e2e8f0", borderRadius: ".375rem", cursor: "pointer", fontSize: ".85rem",
  } as React.CSSProperties,
  activeYear: {
    padding: ".4rem .85rem", background: "#1e40af", color: "white",
    border: "none", borderRadius: ".375rem", cursor: "pointer", fontWeight: 600, fontSize: ".85rem",
  } as React.CSSProperties,
  barBg: { height: 6, borderRadius: 3, background: "#e2e8f0", overflow: "hidden", flex: 1 } as React.CSSProperties,
  barFill: (pct: number, clr: string): React.CSSProperties => ({
    height: "100%", width: Math.min(pct, 100) + "%", background: clr,
    borderRadius: 3, transition: "width .4s ease",
  }),
  badge: (clr: string): React.CSSProperties => ({
    display: "inline-block", padding: ".15rem .5rem", borderRadius: "9999px",
    fontSize: ".75rem", fontWeight: 500, background: clr + "18", color: clr,
  }),
  empty: { padding: "2rem", textAlign: "center" as const, color: "#94a3b8", fontSize: ".9rem" } as React.CSSProperties,
  tabs: {
    display: "flex", gap: ".25rem", marginBottom: "1rem",
    borderBottom: "1px solid #e2e8f0",
  } as React.CSSProperties,
  tab: (active: boolean): React.CSSProperties => ({
    padding: ".6rem 1rem", border: "none",
    borderBottom: active ? "3px solid #3b82f6" : "3px solid transparent",
    background: "transparent", cursor: "pointer",
    fontWeight: active ? 600 : 400, color: active ? "#3b82f6" : "#64748b", fontSize: ".9rem",
  }),
}

// ── KPI Card ───────────────────────────────────────────

const KpiCard: React.FC<{ label: string; value: string; color: string; testId: string }> = ({
  label, value, color, testId,
}) => (
  <div style={s.kpiCard(color)} data-testid={testId}>
    <span style={s.kpiVal(color)}>{value}</span>
    <span style={s.kpiLbl}>{label}</span>
  </div>
)

// ── Tabs ───────────────────────────────────────────────

type Tab = "overview" | "yearly" | "services" | "partners"

const TAB_LABELS: Record<Tab, string> = {
  overview: "Vue d'ensemble",
  yearly: "Par année",
  services: "Par service",
  partners: "Par partenaire",
}

// ── Component ──────────────────────────────────────────

const AccountingDashboard = () => {
  const [summary, setSummary] = useState<Summary | null>(null)
  const [yearly, setYearly] = useState<YearlyRow[]>([])
  const [monthly, setMonthly] = useState<MonthlyRow[]>([])
  const [services, setServices] = useState<ServiceRow[]>([])
  const [statuses, setStatuses] = useState<StatusRow[]>([])
  const [partners, setPartners] = useState<PartnerRow[]>([])
  const [selectedYear, setSelectedYear] = useState<number | null>(null)
  const [activeTab, setActiveTab] = useState<Tab>("overview")
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    void loadAll()
  }, [])

  const loadAll = async () => {
    setLoading(true)
    const [summaryRes, yearlyRes, servicesRes, statusRes, partnerRes] = await Promise.all([
      supabase.rpc("accounting_summary"),
      supabase.rpc("revenue_by_year"),
      supabase.rpc("revenue_by_service"),
      supabase.rpc("revenue_by_status"),
      supabase.rpc("revenue_by_partner"),
    ])

    if (summaryRes.data) {
      const raw = Array.isArray(summaryRes.data) ? summaryRes.data[0] : summaryRes.data
      if (raw) setSummary(raw as Summary)
    }
    if (yearlyRes.data) setYearly(yearlyRes.data as YearlyRow[])
    if (servicesRes.data) setServices(servicesRes.data as ServiceRow[])
    if (statusRes.data) setStatuses(statusRes.data as StatusRow[])
    if (partnerRes.data) setPartners(partnerRes.data as PartnerRow[])

    if (yearlyRes.data && (yearlyRes.data as YearlyRow[]).length > 0) {
      const firstYear = (yearlyRes.data as YearlyRow[])[0].year
      await loadMonthly(firstYear)
    }
    setLoading(false)
  }

  const loadMonthly = async (year: number) => {
    setSelectedYear(year)
    const { data } = await supabase.rpc("revenue_by_month", { p_year: year })
    if (data) setMonthly(data as MonthlyRow[])
  }

  if (loading) {
    return (
      <div style={s.page}>
        <h1 style={s.title}>📊 Comptabilité</h1>
        <p style={{ color: "#94a3b8" }}>Chargement des données…</p>
      </div>
    )
  }

  const maxServiceRev = services.length > 0 ? Math.max(...services.map(x => x.revenue)) : 1
  const maxMonthRev = monthly.length > 0 ? Math.max(...monthly.map(x => x.revenue)) : 1

  return (
    <div style={s.page} data-testid="accounting-dashboard">
      <h1 style={s.title}>📊 Comptabilité</h1>

      {/* ── KPIs ── */}
      {summary && (
        <div style={s.kpiGrid} data-testid="kpi-grid">
          <KpiCard label="Chiffre d'affaires" value={chf(summary.total_revenue)} color="#22c55e" testId="kpi-revenue" />
          <KpiCard label="Commandes" value={String(summary.total_orders)} color="#3b82f6" testId="kpi-orders" />
          <KpiCard label="Payées" value={String(summary.total_paid)} color="#22c55e" testId="kpi-paid" />
          <KpiCard label="En attente" value={String(summary.total_pending)} color="#eab308" testId="kpi-pending" />
          <KpiCard label="Panier moyen" value={chf(summary.avg_order_value)} color="#8b5cf6" testId="kpi-avg" />
          <KpiCard label="Réductions coupons" value={chf(summary.total_coupon_discounts)} color="#f97316" testId="kpi-discounts" />
          <KpiCard label="Commissions partenaires" value={chf(summary.total_partner_commissions)} color="#06b6d4" testId="kpi-commissions" />
          <KpiCard label="Via partenaires" value={summary.partner_orders + " cmd"} color="#6366f1" testId="kpi-partner-orders" />
        </div>
      )}

      {/* ── Tabs ── */}
      <div style={s.tabs}>
        {(["overview", "yearly", "services", "partners"] as Tab[]).map(tab => (
          <button key={tab} style={s.tab(activeTab === tab)} onClick={() => setActiveTab(tab)} data-testid={"tab-" + tab}>
            {TAB_LABELS[tab]}
          </button>
        ))}
      </div>

      {/* ── Tab: Vue d'ensemble ── */}
      {activeTab === "overview" && (
        <>
          {/* Statuses */}
          <div style={s.section}>
            <div style={s.sectionHead}><h2 style={s.sectionTitle}>Répartition par statut</h2></div>
            <div style={s.card}>
              {statuses.length === 0 ? (
                <p style={s.empty}>Aucune commande.</p>
              ) : (
                <table style={s.table} data-testid="status-table">
                  <thead>
                    <tr>
                      <th style={s.th}>Statut</th>
                      <th style={s.thR}>Commandes</th>
                      <th style={s.thR}>CA (CHF)</th>
                      <th style={{ ...s.th, width: "30%" }}>Proportion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statuses.map(row => {
                      const pct = summary ? (row.order_count / summary.total_orders) * 100 : 0
                      return (
                        <tr key={row.status}>
                          <td style={s.td}>{STATUS_LABELS[row.status] || row.status}</td>
                          <td style={s.tdR}>{row.order_count}</td>
                          <td style={s.tdB}>{chf(row.revenue)}</td>
                          <td style={s.td}>
                            <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
                              <div style={s.barBg}><div style={s.barFill(pct, "#3b82f6")} /></div>
                              <span style={{ fontSize: ".8rem", color: "#64748b", minWidth: "3rem", textAlign: "right" }}>{pct.toFixed(0)}%</span>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Monthly detail */}
          {selectedYear && (
            <div style={s.section}>
              <div style={s.sectionHead}>
                <h2 style={s.sectionTitle}>Détail mensuel — {selectedYear}</h2>
                <div style={{ display: "flex", gap: ".25rem" }}>
                  {yearly.map(y => (
                    <button key={y.year} style={y.year === selectedYear ? s.activeYear : s.btnGhost}
                      onClick={() => loadMonthly(y.year)} data-testid={"year-btn-" + y.year}>
                      {y.year}
                    </button>
                  ))}
                </div>
              </div>
              <div style={s.card}>
                {monthly.length === 0 ? (
                  <p style={s.empty}>Aucune donnée pour {selectedYear}.</p>
                ) : (
                  <table style={s.table} data-testid="monthly-table">
                    <thead>
                      <tr>
                        <th style={s.th}>Mois</th>
                        <th style={s.thR}>Commandes</th>
                        <th style={s.thR}>Payées</th>
                        <th style={s.thR}>Panier moyen</th>
                        <th style={s.thR}>CA (CHF)</th>
                        <th style={{ ...s.th, width: "20%" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthly.map(m => {
                        const pct = maxMonthRev > 0 ? (m.revenue / maxMonthRev) * 100 : 0
                        return (
                          <tr key={m.month}>
                            <td style={s.td}>{MONTH_NAMES[m.month] || m.month}</td>
                            <td style={s.tdR}>{m.order_count}</td>
                            <td style={s.tdR}>{m.paid_count}</td>
                            <td style={s.tdR}>{chf(m.avg_order_value)}</td>
                            <td style={s.tdB}>{chf(m.revenue)}</td>
                            <td style={s.td}>
                              <div style={s.barBg}><div style={s.barFill(pct, "#22c55e")} /></div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Tab: Par année ── */}
      {activeTab === "yearly" && (
        <div style={s.section}>
          <div style={s.card}>
            {yearly.length === 0 ? (
              <p style={s.empty}>Aucune donnée annuelle.</p>
            ) : (
              <table style={s.table} data-testid="yearly-table">
                <thead>
                  <tr>
                    <th style={s.th}>Année</th>
                    <th style={s.thR}>Commandes</th>
                    <th style={s.thR}>Payées</th>
                    <th style={s.thR}>Panier moyen</th>
                    <th style={s.thR}>Réductions</th>
                    <th style={s.thR}>Commissions</th>
                    <th style={s.thR}>CA (CHF)</th>
                  </tr>
                </thead>
                <tbody>
                  {yearly.map(y => (
                    <tr key={y.year} data-testid={"yearly-row-" + y.year}>
                      <td style={s.td}>
                        <button style={y.year === selectedYear ? s.activeYear : s.btn}
                          onClick={() => { loadMonthly(y.year); setActiveTab("overview") }}>
                          {y.year}
                        </button>
                      </td>
                      <td style={s.tdR}>{y.order_count}</td>
                      <td style={s.tdR}>{y.paid_count}</td>
                      <td style={s.tdR}>{chf(y.avg_order_value)}</td>
                      <td style={s.tdR}>
                        {y.coupon_discounts > 0
                          ? <span style={s.badge("#f97316")}>-{chf(y.coupon_discounts)}</span>
                          : <span style={{ color: "#94a3b8" }}>—</span>}
                      </td>
                      <td style={s.tdR}>
                        {y.partner_commissions > 0
                          ? <span style={s.badge("#06b6d4")}>{chf(y.partner_commissions)}</span>
                          : <span style={{ color: "#94a3b8" }}>—</span>}
                      </td>
                      <td style={s.tdB}>{chf(y.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: Par service ── */}
      {activeTab === "services" && (
        <div style={s.section}>
          <div style={s.card}>
            {services.length === 0 ? (
              <p style={s.empty}>Aucune donnée par service.</p>
            ) : (
              <table style={s.table} data-testid="services-table">
                <thead>
                  <tr>
                    <th style={s.th}>Service</th>
                    <th style={s.thR}>Commandes</th>
                    <th style={s.thR}>CA (CHF)</th>
                    <th style={{ ...s.th, width: "30%" }}>Part du CA</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map(row => {
                    const pct = maxServiceRev > 0 ? (row.revenue / maxServiceRev) * 100 : 0
                    return (
                      <tr key={row.service_name}>
                        <td style={s.td}>{row.service_name}</td>
                        <td style={s.tdR}>{row.order_count}</td>
                        <td style={s.tdB}>{chf(row.revenue)}</td>
                        <td style={s.td}>
                          <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
                            <div style={s.barBg}><div style={s.barFill(pct, "#8b5cf6")} /></div>
                            <span style={{ fontSize: ".8rem", color: "#64748b", minWidth: "3rem", textAlign: "right" }}>{pct.toFixed(0)}%</span>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: Par partenaire ── */}
      {activeTab === "partners" && (
        <div style={s.section}>
          <div style={s.card}>
            {partners.length === 0 ? (
              <p style={s.empty} data-testid="no-partner-data">Aucune commande partenaire.</p>
            ) : (
              <table style={s.table} data-testid="partners-table">
                <thead>
                  <tr>
                    <th style={s.th}>Partenaire</th>
                    <th style={s.thR}>Commandes</th>
                    <th style={s.thR}>Commissions</th>
                    <th style={s.thR}>CA (CHF)</th>
                  </tr>
                </thead>
                <tbody>
                  {partners.map(p => (
                    <tr key={p.partner_id}>
                      <td style={s.td}>{p.partner_name}</td>
                      <td style={s.tdR}>{p.order_count}</td>
                      <td style={s.tdR}><span style={s.badge("#06b6d4")}>{chf(p.commission_total)}</span></td>
                      <td style={s.tdB}>{chf(p.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default AccountingDashboard
